import { Button, Grid, Tooltip } from '@mui/material';
import React, { ReactNode, useContext, useState } from 'react'
import IconsColor from '../../utility/iconsColor';
import { DateRangePicker, FocusedInputShape } from 'react-dates';
import { Moment } from 'moment';
import styles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import DatePresets from '../callAnalysis/datePresets';
import CloseIcon from '@mui/icons-material/Close';
import globalStyle from "../../../assets/styles/global.module.scss";
import { UserContext, UserContextPayload } from '../../../context/userContext';
import DownloadIcon from '@mui/icons-material/Download';
import { chatLogsIntialData, closedChats, threadMetaData, whatsAppChatsData } from '../../../models/axiosModel';
import { formatDateAccordingToTimezoneUsingMoment, formatDateTime, isReturningUser } from '../../../utils/constants';
import { primaryColor } from '../../../utils/colors';

interface DateFilterProps {
    filterChatsByDate: (start_date: Moment | null, end_date: Moment | null) => void,
    flagMobile?: boolean,
    subType: string,
    filteredData: chatLogsIntialData
}

const DateFilter: React.FC<DateFilterProps> = (props) => {

    const { filterChatsByDate, flagMobile, subType, filteredData } = props;
    const { userDetail, setFlagLoading } = useContext(UserContext) as UserContextPayload;

    const dateFormat: string = "DD/MM/YYYY";
    const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [endDate, setEndDate] = useState<Moment | null>(null);

    const renderDateFilterBtn = (): ReactNode => {
        if (!startDate && !endDate) {
            return (
                <Grid item className={`${globalStyle.displayFlexStyle} ${globalStyle.alignItemCenter} ${globalStyle.primaryFontSize} ${globalStyle.primaryFontWeight}`}>
                    Date Filter
                </Grid>
            );
        }
        else if (startDate && !endDate) {
            return (
                <Grid item>
                    <Button
                        className={styles.filterButtons}
                        endIcon={
                            <CloseIcon
                                onClick={() => {
                                    setStartDate(null)
                                    filterChatsByDate(null, endDate);
                                }}
                            />
                        }
                    >
                        {formatDateTime(startDate, "ymd")}
                    </Button>
                </Grid>

            )
        } else {
            return (
                <Grid item>
                    <Button
                        className={styles.filterButtons}
                        endIcon={
                            <CloseIcon
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                    filterChatsByDate(null, null);
                                }}
                            />
                        }
                    >
                        {`${formatDateTime(startDate, "ymd")} to ${formatDateTime(endDate, "ymd")}`}
                    </Button>
                </Grid>
            )
        }
    }

    const switchCaseOfWhatsAppChats = (header: string, chat: whatsAppChatsData): string => {
        switch (header) {
            case "Phone Number":
                return chat.thread_metadata?.find((obj: threadMetaData) => obj?.metadata_type_name == 'phone_number')?.metadata_value || "";
            case "User Name":
                return chat.thread_metadata?.find((obj: threadMetaData) => obj?.metadata_type_name == 'name')?.metadata_value || "";
            case "Start Timestamp":
                return formatDateAccordingToTimezoneUsingMoment(chat.thread_start_timestamp, userDetail.timezone_hour) || "";
            case "Last Timestamp":
                return formatDateAccordingToTimezoneUsingMoment(chat.thread_last_updated_timestamp, userDetail.timezone_hour) || "";
            case "Returning User":
                return isReturningUser(formatDateAccordingToTimezoneUsingMoment(chat.thread_start_timestamp, userDetail.timezone_hour), formatDateAccordingToTimezoneUsingMoment(chat.thread_last_updated_timestamp, userDetail.timezone_hour)) ? "Yes" : "No";
            default:
                return "";
        }
    }

    const switchCaseOfClosedChats = (header: string, chat: closedChats) => {
        switch (header) {
            case "Phone Number":
                return chat.chatbot_phone_number || "";
            case "User Name":
                return chat.chatbot_user_name || "";
            case "Start Timestamp":
                return formatDateAccordingToTimezoneUsingMoment(chat.chatbot_thread_data?.[0]?.timestamp, userDetail.timezone_hour) || "";
            case "Last Timestamp":
                return formatDateAccordingToTimezoneUsingMoment(chat.last_message_time, userDetail.timezone_hour) || "";
            case "Returning User":
                return isReturningUser(formatDateAccordingToTimezoneUsingMoment(chat.chatbot_thread_data?.[0]?.timestamp, userDetail.timezone_hour), formatDateAccordingToTimezoneUsingMoment(chat.last_message_time, userDetail.timezone_hour)) ? "Yes" : "No";
            default:
                return "";
        }
    }

    const downloadChats = () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const headers = ['Phone Number', 'User Name', 'Start Timestamp', 'Last Timestamp', 'Returning User'];
        let rows: string[][] = [[]];
        if (subType == "Active") {
            if (filteredData?.activeChatsFiltered?.length > 0) {
                rows = filteredData?.activeChatsFiltered?.map((chat: closedChats | whatsAppChatsData) => {
                    const row: string[] = [];
                    headers.forEach(header => {
                        if ("flag_new" in chat) {
                            row.push(switchCaseOfWhatsAppChats(header, chat));
                        } else {
                            row.push(switchCaseOfClosedChats(header, chat));
                        }
                    });
                    return row;
                })
            }
        } else {
            if (filteredData?.closeChatsFiltered?.length > 0) {
                rows = filteredData?.closeChatsFiltered?.map((chat: closedChats) => {
                    const row: string[] = [];
                    headers.forEach(header => {
                        row.push(switchCaseOfClosedChats(header, chat));
                    })
                    return row;
                })
            }
        }

        if (filteredData?.activeChatsFiltered?.length > 0 || filteredData?.closeChatsFiltered?.length > 0) {
            const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `user_data.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    return (
        <>
            <Grid container justifyContent={"space-between"} className={`${globalStyle.relativePosition}`}>
                {renderDateFilterBtn()}
                <Grid item className={`${globalStyle.clickable}`}>
                    <Grid container flexWrap={"nowrap"} alignItems={"center"} gap="5px">
                        <Grid item className={`${globalStyle.displayFlexStyle} ${globalStyle.alignItemCenter}`} onClick={() => setFocusedInput('startDate')}>
                            <IconsColor iconName="calender" height={"18px"} width={"18px"} fillColor={primaryColor} />
                        </Grid>
                        <Grid item className={`${styles.dateRangePickerClass}`}>
                            <DateRangePicker
                                startDate={startDate}
                                startDateId="start_date"
                                endDate={endDate}
                                endDateId="end_date"
                                onDatesChange={({ startDate, endDate }) => {
                                    setStartDate(startDate);
                                    setEndDate(endDate);
                                    filterChatsByDate(startDate, endDate);
                                    if (endDate) {
                                        setFocusedInput(null);
                                    }
                                }}
                                focusedInput={focusedInput}
                                onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                                displayFormat={dateFormat}
                                isOutsideRange={() => false}
                                numberOfMonths={flagMobile ? 1 : 2}
                                renderCalendarInfo={() => (
                                    <DatePresets
                                        startDate={startDate}
                                        endDate={endDate}
                                        dateFormat={dateFormat}
                                        handlePresets={(start: Moment, end: Moment) => {
                                            setFocusedInput(null);
                                            setStartDate(start);
                                            setEndDate(end);
                                            filterChatsByDate(start, end);
                                        }}
                                        flagCloseBtn={true}
                                        handleCloseBtn={() => {
                                            setFocusedInput(null);
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {userDetail?.user_organization_data?.organization_id === 2545 &&  // aapdujunagadh
                            <Grid item display={"flex"} onClick={() => downloadChats()}>
                                <Tooltip title="Download" placement='bottom'>
                                    <DownloadIcon className={`${globalStyle.primaryColor}`} />
                                </Tooltip>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default DateFilter
